// ./src/components/generic/SelectableFileCard/SelectableFileCard.tsx
import React, { useMemo, useCallback } from "react";
import {
  Card,
  Image,
  Text,
  Box,
  Center,
  Checkbox,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  formatFileSize,
  getFileIcon,
  isPreviewableImage,
  truncateFilename,
} from "@/utils/fileHelpers";

import { UploadedFile } from "@/utils/api/UploadedFileService";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface SelectableFileCardProps {
  /** The file to display */
  file: UploadedFile;

  /** Whether the file is currently selected */
  selected: boolean;

  /** Whether the file is disabled (e.g., already attached) */
  disabled?: boolean;

  /** Callback when selection changes */
  onSelect: (fileId: string, selected: boolean) => void;

  /** Card size variant */
  size?: "sm" | "md" | "lg";

  /** Tooltip text when disabled */
  disabledTooltip?: string;

  /** Whether the card should fill its container width */
  fullWidth?: boolean;

  /**
   * A title shown INSTEAD of the file name, with no size / date line — for a ready-made picture (the brand's gallery),
   * where the file name, its size and the day it was provisioned mean nothing to the host.
   */
  caption?: string;
}

// ============================================
// Size Configurations
// ============================================

interface SizeConfig {
  cardWidth: number | string;
  thumbnailHeight: number;
  iconSize: number;
  fontSize: "xs" | "sm" | "md";
  padding: "xs" | "sm" | "md";
  checkboxSize: "xs" | "sm" | "md";
  maxFilenameLength: number;
}

const SIZE_CONFIGS: Record<"sm" | "md" | "lg", SizeConfig> = {
  sm: {
    cardWidth: 140,
    thumbnailHeight: 80,
    iconSize: 28,
    fontSize: "xs",
    padding: "xs",
    checkboxSize: "sm",
    maxFilenameLength: 15,
  },
  md: {
    cardWidth: 180,
    thumbnailHeight: 100,
    iconSize: 36,
    fontSize: "sm",
    padding: "sm",
    checkboxSize: "md",
    maxFilenameLength: 20,
  },
  lg: {
    cardWidth: 220,
    thumbnailHeight: 120,
    iconSize: 44,
    fontSize: "md",
    padding: "md",
    checkboxSize: "md",
    maxFilenameLength: 25,
  },
};

// ============================================
// Component
// ============================================

export const SelectableFileCard: React.FC<SelectableFileCardProps> = ({
  file,
  selected,
  disabled = false,
  onSelect,
  size = "md",
  disabledTooltip,
  fullWidth = false,
  caption,
}) => {
  const { t } = useTranslation(["common"]);
  const theme = useMantineTheme();

  // Get size configuration
  const config = SIZE_CONFIGS[size];

  // Determine file properties
  const isImage = useMemo(
    () => isPreviewableImage(file.mimeType),
    [file.mimeType]
  );
  const fileIcon = useMemo(() => getFileIcon(file.mimeType), [file.mimeType]);

  // Get the image URL for preview (prefer thumbnail for cards)
  const previewUrl = file.thumbnailUrl || file.fileUrl;

  // Full filename for tooltip
  const fullName = caption || file.name || file.originalFilename || "Unknown file";

  // Truncated filename for display (a caption is shown whole — it is already a title)
  const displayName = useMemo(
    () => (caption ? caption : truncateFilename(fullName, config.maxFilenameLength)),
    [caption, fullName, config.maxFilenameLength]
  );

  // Formatted file size
  const formattedSize = useMemo(
    () => formatFileSize(file.sizeBytes),
    [file.sizeBytes]
  );

  // Formatted date
  const formattedDate = useMemo(() => {
    if (!file.createdAt) return null;
    return dayjs(file.createdAt).format("MMM D, YYYY");
  }, [file.createdAt]);

  // Handle card click
  const handleClick = useCallback(() => {
    if (!disabled && file.id) {
      onSelect(file.id, !selected);
    }
  }, [disabled, file.id, selected, onSelect]);

  // Handle checkbox change (prevent double-firing with card click)
  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (!disabled && file.id) {
        onSelect(file.id, event.currentTarget.checked);
      }
    },
    [disabled, file.id, onSelect]
  );

  // Card styles based on state
  const cardStyles = useMemo(() => {
    const baseStyles: React.CSSProperties = {
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      transition: "all 0.15s ease",
      overflow: "hidden",
    };

    if (selected && !disabled) {
      return {
        ...baseStyles,
        borderColor: theme.colors.blue[5],
        boxShadow: `0 0 0 2px ${theme.colors.blue[2]}`,
      };
    }

    return baseStyles;
  }, [disabled, selected, theme.colors.blue]);

  // Determine card width
  const cardWidth = fullWidth ? "100%" : config.cardWidth;

  // Render the card content
  const cardContent = (
    <Card
      shadow="sm"
      padding={config.padding}
      radius="md"
      withBorder
      w={cardWidth}
      style={cardStyles}
      onClick={handleClick}
    >
      {/* Thumbnail / Icon area */}
      <Card.Section>
        <Box h={config.thumbnailHeight} bg="gray.1" pos="relative">
          {isImage && previewUrl ? (
            <Image
              src={previewUrl}
              alt={fullName}
              h={config.thumbnailHeight}
              fit="cover"
              style={{
                filter: disabled ? "grayscale(50%)" : "none",
              }}
            />
          ) : (
            <Center h="100%">
              <AppIcon
                iconName={fileIcon}
                size={config.iconSize}
                style={{ color: theme.colors.gray[disabled ? 4 : 5] }}
              />
            </Center>
          )}

          {/* Checkbox overlay */}
          <Box
            pos="absolute"
            top={8}
            right={8}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              disabled={disabled}
              size={config.checkboxSize}
              styles={{
                input: {
                  backgroundColor: selected
                    ? theme.colors.blue[6]
                    : "rgba(255, 255, 255, 0.9)",
                  borderColor: selected
                    ? theme.colors.blue[6]
                    : theme.colors.gray[4],
                  cursor: disabled ? "not-allowed" : "pointer",
                },
              }}
            />
          </Box>

          {/* Disabled overlay */}
          {disabled && (
            <Box
              pos="absolute"
              bottom={0}
              left={0}
              right={0}
              bg="rgba(0,0,0,0.5)"
              py={4}
              px={8}
            >
              <Text size="md" c="white" ta="center">
                {disabledTooltip || t("common:attachments.alreadyAttached")}
              </Text>
            </Box>
          )}
        </Box>
      </Card.Section>

      {/* File info */}
      <Box mt={config.padding}>
        <Tooltip label={fullName} disabled={fullName === displayName}>
          <Text
            size={config.fontSize}
            fw={500}
            lineClamp={1}
            c={disabled ? "dimmed" : undefined}
          >
            {displayName}
          </Text>
        </Tooltip>
        {!caption && (
          <Text size="xs" c="dimmed">
            {formattedSize}
            {formattedDate && ` • ${formattedDate}`}
          </Text>
        )}
      </Box>
    </Card>
  );

  // Wrap with tooltip if disabled
  if (disabled && disabledTooltip) {
    return (
      <Tooltip label={disabledTooltip} position="top">
        <Box>{cardContent}</Box>
      </Tooltip>
    );
  }

  return cardContent;
};
